import { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { defaultCountries, parseCountry } from "react-international-phone";
import { CustomInput } from "global/components";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { AddContactFields } from "models/interfaces";
import contactsStyles from "../Contacts.styles";
import contactsTokens, { ContactAvatarTint } from "../Contacts.tokens";
import { getContactInitials } from "./ContactsList";
import { BackIcon, ChevronDownIcon, TrashIcon } from "./ContactsIcons";
import { tourAttr } from "tour";

const styles = contactsStyles;
const { detail, formActions } = contactsTokens;

/** Character budget the API enforces per field, shown in the inline counter. */
const MAX_LENGTH = {
  name: 100,
  email: 100,
  phone: 50,
  text: 150,
  managerEmail: 255,
  address: 100,
} as const;

/** "4/100" pinned inside the right edge of a field, per the design. */
const Counter = ({ value, max }: { value?: string; max: number }) => (
  <InputAdornment position="end">
    <Typography component="span" sx={styles.counter}>
      {value?.length ?? 0}/{max}
    </Typography>
  </InputAdornment>
);

interface ContactDetailFormProps {
  form: AddContactFields;
  errors: { [key: string]: any };
  tint: ContactAvatarTint;
  isCreatingNew: boolean;
  /** False when the user may look at the contact but not edit it. */
  canEdit: boolean;
  /** The email is the contact's key, so it is only writable while creating. */
  canEditEmail: boolean;
  /**
   * Off until `ss-core-service` exposes a delete-contact endpoint — there is no
   * `DELETE /contact/...` today, so no caller can supply a working `onDelete`.
   * Both props are ready for the screen to pass once it lands.
   */
  canDelete?: boolean;
  isSaving: boolean;
  onChangeField: (event: any) => void;
  onChangeAddressField: (event: any) => void;
  onChangeCountry: (country: string) => void;
  onChangePhone: (phone: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  /** Mobile renders the detail pane on its own, with a back affordance. */
  showBackButton: boolean;
  onBack: () => void;
}

const ContactDetailForm = ({
  form,
  errors,
  tint,
  isCreatingNew,
  canEdit,
  canEditEmail,
  canDelete,
  isSaving,
  onChangeField,
  onChangeAddressField,
  onChangeCountry,
  onChangePhone,
  onSave,
  onCancel,
  onDelete,
  showBackButton,
  onBack,
}: ContactDetailFormProps) => {
  const countries = useMemo(
    () =>
      defaultCountries
        .map((entry) => parseCountry(entry))
        .map((entry) => ({ name: entry.name, iso2: entry.iso2 })),
    [],
  );

  /**
   * The desktop's "Enter contact number" runs past the phone and tablet field
   * boxes, which are 100px+ narrower; both references label the placeholder
   * simply "Contact number", so use theirs below `md`.
   */
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const fullName = `${form.firstName ?? ""} ${form.lastName ?? ""}`.trim();
  const country = form.address?.country ?? "";
  /** Stored values predating this list still need an option to select. */
  const isKnownCountry = countries.some((entry) => entry.name === country);

  return (
    <Box sx={styles.detailPane} {...tourAttr("contacts.details")}>
      <Box sx={styles.detailInner}>
        {/* identity strip */}
        <Box sx={styles.detailHead}>
          {showBackButton && (
            <IconButton
              aria-label="Back to contacts"
              onClick={onBack}
              sx={styles.detailBackButton}
            >
              <BackIcon
                sx={{ width: detail.backIconSize, height: detail.backIconSize }}
              />
            </IconButton>
          )}
          <Avatar
            aria-hidden="true"
            sx={styles.detailAvatar(
              isCreatingNew ? detail.newAvatarBg : tint.bg,
              isCreatingNew ? detail.newAvatarColor : tint.color,
            )}
          >
            {isCreatingNew ? "+" : getContactInitials(form)}
          </Avatar>
          <Box minWidth={0}>
            <Typography component="h2" sx={styles.detailTitle}>
              {isCreatingNew ? "New Contact" : fullName}
            </Typography>
            <Typography component="div" sx={styles.detailSubtitle}>
              {isCreatingNew
                ? "Add someone you send documents to."
                : form.emailId}
            </Typography>
          </Box>
        </Box>

        {/* The tablet reference sets the whole form on a white card; the phone
            and the desktop draw it straight on their pane, so this box is
            chrome-less there. */}
        <Box sx={styles.formCard}>
          {/* names */}
          <Box sx={styles.fieldGrid}>
            <CustomInput
              required
              label="First Name"
              id="firstName"
              name="firstName"
              type="text"
              placeHolder="First name"
              value={form.firstName ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.name }}
              InputProps={{
                endAdornment: (
                  <Counter value={form.firstName} max={MAX_LENGTH.name} />
                ),
              }}
              error={errors.firstName || ""}
            />
            <CustomInput
              required
              label="Last Name"
              id="lastName"
              name="lastName"
              type="text"
              placeHolder="Last name"
              value={form.lastName ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.name }}
              InputProps={{
                endAdornment: (
                  <Counter value={form.lastName} max={MAX_LENGTH.name} />
                ),
              }}
              error={errors.lastName || ""}
            />
          </Box>

          {/* email */}
          <Box sx={styles.fieldBlock}>
            <CustomInput
              required
              label="Email"
              id="emailId"
              name="emailId"
              type="email"
              placeHolder="name@company.com"
              value={form.emailId ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              // The address is the contact's key, so it is only writable while
              // creating. `readOnly` rather than `disabled` — the design shows it
              // with the same fill and ink as every other field.
              propsToInputElement={{
                maxLength: MAX_LENGTH.email,
                readOnly: !canEditEmail,
              }}
              InputProps={{
                endAdornment: (
                  <Counter value={form.emailId} max={MAX_LENGTH.email} />
                ),
              }}
              error={errors.emailId || ""}
            />
          </Box>

          {/*
            Contact number, job title, company and department share one grid so
            the phone column can re-pair them: the reference gives the number a
            whole row down there and sets Job Title beside Company instead of
            following the desktop's (number | job) and (company | department).
          */}
          <Box sx={styles.fieldGridBlock}>
            <Box sx={styles.fieldSpanRow}>
              <CustomContactNumberInput
                label="Contact Number"
                id="contactNo"
                placeHolder={
                  isCompact ? "Contact number" : "Enter contact number"
                }
                value={form.contactNo ?? ""}
                onChange={onChangePhone}
                disabled={!canEdit}
                labelSx={styles.fieldLabel}
                labelSpacing={styles.labelSpacing}
                sx={styles.phoneField}
                inputProps={{ maxLength: MAX_LENGTH.phone }}
                InputProps={{
                  endAdornment: (
                    <Counter value={form.contactNo} max={MAX_LENGTH.phone} />
                  ),
                }}
                error={errors.contactNo || ""}
              />
            </Box>
            <CustomInput
              label="Job Title"
              id="jobTitle"
              name="jobTitle"
              type="text"
              placeHolder="Job title"
              value={form.jobTitle ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.text }}
              InputProps={{
                endAdornment: (
                  <Counter value={form.jobTitle} max={MAX_LENGTH.text} />
                ),
              }}
              error={errors.jobTitle || ""}
            />
            <CustomInput
              label="Company Name"
              id="companyName"
              name="companyName"
              type="text"
              placeHolder="Company name"
              value={form.companyName ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.text }}
              InputProps={{
                endAdornment: (
                  <Counter value={form.companyName} max={MAX_LENGTH.text} />
                ),
              }}
              error={errors.companyName || ""}
            />
            <Box sx={styles.fieldSpanRow}>
              <CustomInput
                label="Department"
                id="department"
                name="department"
                type="text"
                placeHolder="Department"
                value={form.department ?? ""}
                onChange={onChangeField}
                disabled={!canEdit}
                sx={styles.textField}
                labelSx={styles.fieldLabel}
                labelSpacing={styles.labelSpacing}
                propsToInputElement={{ maxLength: MAX_LENGTH.text }}
                InputProps={{
                  endAdornment: (
                    <Counter value={form.department} max={MAX_LENGTH.text} />
                  ),
                }}
                error={errors.department || ""}
              />
            </Box>
          </Box>

          {/* manager email */}
          <Box sx={styles.fieldBlock}>
            <CustomInput
              label="Manager Email"
              id="managerEmail"
              name="managerEmail"
              type="email"
              placeHolder="manager@example.com"
              value={form.managerEmail ?? ""}
              onChange={onChangeField}
              disabled={!canEdit}
              sx={styles.textField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.managerEmail }}
              InputProps={{
                endAdornment: (
                  <Counter
                    value={form.managerEmail}
                    max={MAX_LENGTH.managerEmail}
                  />
                ),
              }}
              error={errors.managerEmail || ""}
            />
          </Box>

          {/* address */}
          <Box sx={styles.sectionHeading}>
            <Typography component="span" sx={styles.sectionHeadingLabel}>
              Address
            </Typography>
            <Box sx={styles.sectionHeadingRule} />
          </Box>
          <CustomInput
            label="Street"
            id="address_street"
            name="street"
            type="text"
            placeHolder="Street"
            value={form.address?.street ?? ""}
            onChange={onChangeAddressField}
            disabled={!canEdit}
            sx={styles.plainTextField}
            labelSx={styles.fieldLabel}
            labelSpacing={styles.labelSpacing}
            propsToInputElement={{ maxLength: MAX_LENGTH.address }}
          />
          <Box sx={styles.fieldGridThirdsBlock}>
            <CustomInput
              label="City"
              id="address_city"
              name="city"
              type="text"
              placeHolder="City"
              value={form.address?.city ?? ""}
              onChange={onChangeAddressField}
              disabled={!canEdit}
              sx={styles.plainTextField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.address }}
            />
            <CustomInput
              label="State"
              id="address_state"
              name="state"
              type="text"
              placeHolder="State"
              value={form.address?.state ?? ""}
              onChange={onChangeAddressField}
              disabled={!canEdit}
              sx={styles.plainTextField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.address }}
            />
            <CustomInput
              label="Zip Code"
              id="address_zip"
              name="zip"
              type="text"
              placeHolder="Zip code"
              value={form.address?.zip ?? ""}
              onChange={onChangeAddressField}
              disabled={!canEdit}
              sx={styles.plainTextField}
              labelSx={styles.fieldLabel}
              labelSpacing={styles.labelSpacing}
              propsToInputElement={{ maxLength: MAX_LENGTH.address }}
            />
            {/*
              Country rides in the same grid as City / State / Zip so the phone
              can sit it beside Zip; from `sm` up it drops onto its own row at
              half width, the way both larger references draw it.
            */}
            <Box sx={styles.countryCell}>
              <Box mb={styles.labelSpacing}>
                <InputLabel
                  id="address_country-label"
                  htmlFor="address_country"
                  sx={styles.fieldLabel}
                >
                  Country
                </InputLabel>
              </Box>
              <Box position="relative" display="flex" alignItems="center">
                <Select
                  id="address_country"
                  labelId="address_country-label"
                  value={country}
                  displayEmpty
                  disabled={!canEdit}
                  IconComponent={() => null}
                  onChange={(event) =>
                    onChangeCountry(event.target.value as string)
                  }
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  renderValue={() =>
                    country ? (
                      <span>{country}</span>
                    ) : (
                      <Box component="span" sx={styles.countryPlaceholder}>
                        Select country
                      </Box>
                    )
                  }
                  sx={styles.countrySelect}
                >
                  {country && !isKnownCountry && (
                    <MenuItem value={country}>{country}</MenuItem>
                  )}
                  {countries.map((entry) => (
                    <MenuItem key={entry.iso2} value={entry.name}>
                      {entry.name}
                    </MenuItem>
                  ))}
                </Select>
                <ChevronDownIcon sx={styles.selectChevron} />
              </Box>
            </Box>
          </Box>

          {/* actions */}
          <Box sx={styles.formActions}>
            {canEdit && (
              <Button
                id="contact_save_btn"
                variant="contained"
                disableElevation
                disabled={isSaving}
                onClick={onSave}
                startIcon={
                  isSaving ? (
                    <CircularProgress
                      size={formActions.spinnerSize}
                      color="inherit"
                    />
                  ) : undefined
                }
                sx={styles.saveButton}
              >
                {isCreatingNew ? "Add Contact" : "Save Changes"}
              </Button>
            )}
            <Button
              id="contact_cancel_btn"
              variant="outlined"
              disabled={isSaving}
              onClick={onCancel}
              sx={styles.cancelButton}
            >
              Cancel
            </Button>
            {!isCreatingNew && canDelete && onDelete && (
              <Button
                id="contact_delete_btn"
                type="button"
                variant="outlined"
                disabled={isSaving}
                onClick={onDelete}
                sx={styles.deleteButton}
              >
                <TrashIcon sx={styles.deleteIcon} />
                Delete Contact
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/** Placeholder shown in the detail pane while a save or a fetch is in flight. */
export const ContactDetailSkeleton = () => (
  <Box aria-hidden="true" sx={styles.detailPane}>
    <Box sx={styles.detailInner}>
      <Box sx={styles.detailHead}>
        <Skeleton variant="circular" sx={styles.skeletonDetailAvatar} />
        <Box flex={1}>
          <Skeleton
            variant="text"
            width="42%"
            height={26}
            sx={styles.skeleton}
          />
          <Skeleton
            variant="text"
            width="58%"
            height={18}
            sx={styles.skeleton}
          />
        </Box>
      </Box>
      <Box sx={styles.formCard}>
        {[2, 1, 2, 2, 1].map((columns, rowIndex) => (
          <Box
            key={`detail-skeleton-row-${rowIndex}`}
            sx={styles.skeletonRow(columns, rowIndex === 0)}
          >
            {Array.from({ length: columns }).map((_, columnIndex) => (
              // Label line then input box — the same two parts a real field
              // has, so the pane does not shrink when the contact arrives.
              <Box key={`detail-skeleton-${rowIndex}-${columnIndex}`}>
                <Skeleton variant="text" sx={styles.skeletonFieldLabel} />
                <Skeleton variant="rectangular" sx={styles.skeletonField} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export default ContactDetailForm;
